import { useState } from 'react';
import {
    HiChevronDown,
    HiChevronRight,
    HiOutlineLightningBolt,
} from 'react-icons/hi';
import EventList from '../../../../traces/timeline/event-list';
import { formatMs, outcomeLabel } from '../../../../traces/timeline/event-model';

/**
 * What happened in the turn a user message triggered, rendered under that
 * message: the same readable event timeline as the Trace page.
 *
 * Collapsed by default - the transcript is the primary content here and the
 * events are the explanation, so they stay out of the way until asked for.
 */
export default function MessageTrace({ group, loading }) {
    const [open, setOpen] = useState(false);

    if (loading) {
        return (
            <div className='mt-1 text-[10px] text-gray-400'>Loading trace&hellip;</div>
        );
    }

    // A turn the bot never processed (human-handled, or spans not yet exported)
    // has nothing to show, and an empty affordance would only be noise.
    if (!group || !group.events || group.events.length === 0) {
        return null;
    }

    const failed = group.status === 'failed' || group.errors > 0;

    return (
        // Right-aligned to sit under the user bubble it belongs to, but with the
        // panel itself left-aligned so the event list stays readable.
        <div className='mt-1 flex flex-col items-end'>
            <button
                type='button'
                onClick={() => setOpen(!open)}
                className={`inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[10px] font-medium transition-colors ${failed
                    ? 'border-red-200 text-red-700 hover:bg-red-50'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
            >
                {open ? <HiChevronDown size={11} /> : <HiChevronRight size={11} />}
                <HiOutlineLightningBolt size={11} />
                <span>{group.modelCalls} model &middot; {group.toolCalls} tools</span>
                <span className='text-gray-400'>&middot;</span>
                <span className='tabular-nums'>{formatMs(group.durationMs)}</span>
                {group.outcome && group.outcome !== 'answered' && (
                    <>
                        <span className='text-gray-400'>&middot;</span>
                        <span>{outcomeLabel(group.outcome)}</span>
                    </>
                )}
                {group.errors > 0 && (
                    <>
                        <span className='text-red-300'>&middot;</span>
                        <span>{group.errors} {group.errors === 1 ? 'error' : 'errors'}</span>
                    </>
                )}
            </button>

            {open && (
                <div className='mt-1 max-h-96 w-full max-w-md overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 text-left'>
                    <EventList turn={group} compact />
                </div>
            )}
        </div>
    );
}
